/**
 * Pricing Message Fixtures
 *
 * Sample pricing message data for testing and documentation
 * Represents pricing/transparency messaging for each product
 */

import type { PricingMessage } from '~/types/pricing';

/**
 * Review product pricing message
 * Emphasizes ROI through cost per review reduction
 */
export const REVIEW_PRICING_MESSAGE: PricingMessage = {
  heading: 'Low-Risk Start, Clear Usage Model',
  description:
    'QxReview uses event-based pricing aligned to usage. Charges apply to pages processed and documents rejected, with no minimum spend and no lock-in commitment.',
  takeaways: [
    {
      text: 'Usage-based charging tied to review activity',
      icon: 'i-heroicons-check-circle',
    },
    {
      text: 'No minimum spend and no lock-in commitment',
      icon: 'i-heroicons-check-circle',
    },
    {
      text: 'Customer spending dashboard for visibility and reconciliation',
      icon: 'i-heroicons-check-circle',
    },
    {
      text: 'Per-tenant commercial rates managed during onboarding',
      icon: 'i-heroicons-check-circle',
    },
  ],
  cta: {
    text: 'Sign-up via Web (Coming Soon)',
    link: '/review-get-started',
    external: false,
  },
};

/**
 * Verify product pricing message
 * Emphasizes audit transparency and cost control
 */
export const VERIFY_PRICING_MESSAGE: PricingMessage = {
  heading: 'Audit-Grade Pricing Transparency',
  description:
    'The Verify product provides complete pricing transparency with itemized costs. You control which audit modules you enable, paying only for what you use.',
  takeaways: [
    {
      text: 'Itemized pricing for each audit module',
      icon: 'i-heroicons-check-circle',
    },
    {
      text: 'Pay only for enabled verification modules',
      icon: 'i-heroicons-check-circle',
    },
    {
      text: 'No licensing fees or mandatory upgrades',
      icon: 'i-heroicons-check-circle',
    },
    {
      text: 'Quarterly cost reviews with customer success team',
      icon: 'i-heroicons-check-circle',
    },
  ],
  cta: {
    text: 'Request Quote',
    link: '/contact?product=verify',
    external: false,
  },
};

/**
 * Insight product pricing message
 * Emphasizes analytics platform transparency and ROI
 */
export const INSIGHT_PRICING_MESSAGE: PricingMessage = {
  heading: 'Analytics Value at Transparent Pricing',
  description:
    'Insight pricing is built on transparency: you pay for data retention and user seats, with no hidden platform fees. Cancel anytime with no penalties.',
  takeaways: [
    {
      text: 'Transparent per-user, per-month seat pricing',
      icon: 'i-heroicons-check-circle',
    },
    {
      text: 'Tiered data retention (30/90/360 days) with clear pricing',
      icon: 'i-heroicons-check-circle',
    },
    {
      text: 'No platform or feature unlock fees',
      icon: 'i-heroicons-check-circle',
    },
    {
      text: 'Month-to-month billing with no long-term contracts required',
      icon: 'i-heroicons-check-circle',
    },
  ],
  cta: {
    text: 'Start Free Trial',
    link: 'https://example.com/trial/insight',
    external: true,
  },
};

/**
 * Pricing messages indexed by product
 */
export const PRICING_FIXTURES: Record<string, PricingMessage> = {
  Review: REVIEW_PRICING_MESSAGE,
  Verify: VERIFY_PRICING_MESSAGE,
  Insight: INSIGHT_PRICING_MESSAGE,
};
